// src/follower/components/MacroSchedule.jsx
// Displays today's high-impact economic events from followerMacroEvent.
// Always renders — shows a "no events" confirmation when today_high_impact is empty.

import React from "react";

const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    yellow: "#ffe066",
    red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";

// Clock icon inline SVG
const ClockIcon = ({ color = C.meta, size = 10 }) => (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="5" cy="5" r="4" stroke={color} strokeWidth="1"/>
        <line x1="5" y1="2.5" x2="5" y2="5" stroke={color} strokeWidth="1" strokeLinecap="round"/>
        <line x1="5" y1="5" x2="7" y2="6.2" stroke={color} strokeWidth="1" strokeLinecap="round"/>
    </svg>
);

// Checkmark icon inline SVG
const CheckIcon = ({ color = C.green, size = 10 }) => (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="5" cy="5" r="4" stroke={color} strokeWidth="1" opacity=".5"/>
        <polyline points="2.8,5.2 4.3,6.7 7.2,3.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// Upcoming risk dot
const RiskDot = ({ color }) => (
    <div style={{
        width:        6,
        height:       6,
        borderRadius: "50%",
        background:   color,
        boxShadow:    `0 0 5px ${color}`,
        flexShrink:   0,
    }} />
);

function EventRow({ ev }) {
    const isOccurred = ev.has_actual;
    const hasWindow  = !!ev.pause_window;
    const isRisk     = !isOccurred && !!ev.pause_type;
    const isHard     = ev.pause_type === "hard_pause";
    const isCaution  = ev.pause_type === "soft_caution";

    // Color scheme per event state
    const nameColor = isOccurred
        ? C.meta
        : isHard    ? C.amber
        : isCaution ? C.yellow
        : C.body;

    const timeColor = isOccurred ? C.meta : C.cyan;

    return (
        <div style={{
            display:       "flex",
            alignItems:    "flex-start",
            gap:            12,
            padding:        "8px 0",
            borderBottom:   `1px solid ${C.border}`,
            opacity:        isOccurred ? 0.55 : 1,
            transition:     "opacity .2s",
        }}>
            {/* Status indicator */}
            <div style={{ width: 18, display: "flex", justifyContent: "center", paddingTop: 2, flexShrink: 0 }}>
                {isOccurred
                    ? <CheckIcon color={C.meta} />
                    : isRisk
                    ? <RiskDot color={isHard ? C.amber : C.yellow} />
                    : <div style={{ width: 6, height: 6, borderRadius: "50%", border: `1px solid ${C.border}`, flexShrink: 0 }} />
                    }
            </div>

            {/* Main content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Event name */}
                <div style={{
                    fontFamily:    FONT,
                    fontSize:      10,
                    color:         nameColor,
                    letterSpacing: ".3px",
                    marginBottom:  3,
                    lineHeight:    1.4,
                }}>
                    {ev.event}
                    {isOccurred && (
                    <span style={{
                            marginLeft:    8,
                            fontSize:      8,
                            color:         C.meta,
                            letterSpacing: ".5px",
                        }}>
                            PRINTED
                        </span>
                        )}
                    {!isOccurred && isHard && (
                    <span style={{
                            marginLeft:    8,
                            fontSize:      8,
                            color:         C.amber,
                            letterSpacing: ".5px",
                            background:    C.amber + "14",
                            padding:       "1px 5px",
                            borderRadius:  2,
                        }}>
                            HARD PAUSE
                        </span>
                        )}
                    {!isOccurred && isCaution && (
                    <span style={{
                            marginLeft:    8,
                            fontSize:      8,
                            color:         C.yellow,
                            letterSpacing: ".5px",
                            background:    C.yellow + "10",
                            padding:       "1px 5px",
                            borderRadius:  2,
                        }}>
                            CAUTION
                        </span>
                        )}
                </div>

                {/* Time + window row */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{
                        fontFamily:    FONT,
                        fontSize:      9,
                        color:         timeColor,
                        letterSpacing: ".3px",
                    }}>
                        {ev.time_et}
                    </span>

                    {hasWindow && (
                    <div style={{
                            display:    "flex",
                            alignItems: "center",
                            gap:        4,
                        }}>
                        <ClockIcon color={isHard ? C.amber : C.yellow} size={9} />
                            <span style={{
                                fontFamily:    FONT,
                                fontSize:      8,
                                color:         isHard ? C.amber + "99" : C.yellow + "99",
                                letterSpacing: ".3px",
                            }}>
                                Pause {ev.pause_window} ET
                            </span>
                    </div>
                        )}
                </div>
            </div>
        </div>
    );
}

// ── Public component ──────────────────────────────────────────────────────────
//
// Props:
//   macro — MacroSummary from followerMacroEvent
//            { is_paused, pause_type, pause_reason, today_high_impact }

export default function MacroSchedule({ macro }) {
    const events = macro?.today_high_impact ?? [];

    return (
        <div style={{
            background:   C.bg1,
            border:       `1px solid ${C.border}`,
            borderRadius: 10,
            overflow:     "hidden",
        }}>
            {/* Header */}
            <div style={{
                padding:      "12px 20px",
                borderBottom: `1px solid ${C.border}`,
                display:      "flex",
                alignItems:   "center",
                gap:          10,
            }}>
                <ClockIcon color={C.meta} size={11} />
                <span style={{
                    fontFamily:    FONT,
                    fontSize:      9,
                    color:         C.meta,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                }}>
                    Today's High-Impact Events
                </span>
                {events.length > 0 && (
                <span style={{
                        marginLeft:    "auto",
                        fontFamily:    FONT,
                        fontSize:      8,
                        color:         C.meta,
                        letterSpacing: ".5px",
                    }}>
                        {events.filter(e => e.has_actual).length}/{events.length} occurred
                    </span>
                    )}
            </div>

            {/* Event list or empty state */}
            {events.length === 0 ? (
            <div style={{
                    padding:    "16px 20px",
                    display:    "flex",
                    alignItems: "center",
                    gap:        10,
                }}>
                <CheckIcon color={C.green} size={11} />
                    <span style={{
                        fontFamily:    FONT,
                        fontSize:      9,
                        color:         C.meta,
                        letterSpacing: ".4px",
                    }}>
                        No high-impact economic events scheduled today — bot is trading without macro restrictions.
                    </span>
            </div>
                ) : (
            <div style={{ padding: "0 20px" }}>
                {events.map((ev, i) => (
                <EventRow key={`${ev.event}-${i}`} ev={ev} />
                    ))}
            </div>
                )}
        </div>
    );
}